import toast from "react-hot-toast";
import {
  getApiException,
  formatApiError,
  friendlyApiMessage,
} from "../constants/apiErrorMessages";

export { getApiException, formatApiError, friendlyApiMessage };

// The Core answers HTTP 200 to almost everything and reports failures in the body as
// { exception: { code: "DP-403", description: "..." } }. These helpers make such a failure
// visible to the user (one toast, de-duplicated) and let callers treat it as an error.

export class ApiError extends Error {
  constructor(exception, category) {
    super((exception && exception.description) || "The request failed.");
    this.name = "ApiError";
    this.code = exception ? exception.code : undefined;
    this.exception = exception;
    this.category = category;
    // The toast has already been shown; callers must not show a second one.
    this.notified = true;
  }
}

const recentlyShown = new Map();
const DEDUPE_WINDOW_MS = 3000;

export const notifyApiError = (exception) => {
  const message = formatApiError(exception);
  const now = Date.now();
  const last = recentlyShown.get(message);
  if (last && now - last < DEDUPE_WINDOW_MS) return;
  recentlyShown.set(message, now);
  toast.error(message, { id: message, duration: 7000 });
};

// POST a JSON body to the Core. Resolves with the parsed body on success; on a Core exception
// (or a non-2xx status) shows the message and rejects with an ApiError, so react-query
// mutations run onError instead of onSuccess.
export const postJson = async (url, { headers, body, category }) => {
  const response = await fetch(url, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(body),
  });
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }
  const exception =
    getApiException(data) ||
    (!response.ok
      ? {
          code: `HTTP-${response.status}`,
          description: response.statusText || "The request failed.",
        }
      : null);
  if (exception) {
    notifyApiError(exception);
    throw new ApiError(exception, category || url);
  }
  return data;
};
